/**
 * ResumableRender — a long WebCodecsPipeline render that survives the tab.
 *
 * Why (comment #1816, 2026-10-02, /video-to-vr): a guest spent a day on an
 * Android phone turning 16–29-minute videos into VR. A side-by-side frame is
 * wider than a phone's H.264 encoder takes (prod: 2560×720 refused 16 of 16,
 * 3840×1080 7 of 7), so the encode is software and runs 30–75 minutes; Chrome
 * unloaded the backgrounded tab at minute 37 and at minute 54 and every frame
 * went with it. Memory was not the cause — a 30-minute run measured flat at
 * 840–915 MB of the whole browser tree. A phone simply does not keep a tab
 * for an hour, so an hour of work must not live in one tab.
 *
 * How: the render is cut into windows of the SOURCE timeline (`segSec`, 60 s
 * by default). Each window is one ordinary pipeline run the caller makes
 * (`runSegment(trimStart, trimEnd, onProgress)` — trim + dropAudio), and its
 * file is MOVED into OPFS under `rr-checkpoints/<key>/` (the pipeline already
 * wrote it to OPFS — a copy would hold the result twice on a phone's disk).
 * At the end the windows are joined without re-encoding (Mp4CopyTrim.concat —
 * byte ranges, no memory ceiling) and the source's own sound track is put back
 * (MP4TrackRemux.replaceAudio — byte ranges again). A tab that dies costs at
 * most the window in flight; the same file with the same settings continues
 * from the last finished window.
 *
 * Measured before building it (Chrome, 20 s and 60 s clips, H.264 and the
 * VP9 rescue): joined file frame for frame equal to a whole render (600/600,
 * 1800/1800), regular frame step, clean decode; a flash-and-click source kept
 * picture and sound together to 0 ms at every mark; four 15-s windows took
 * 15.4 s against 23.8 s for the whole run — the cut costs nothing measurable.
 *
 * The key is the caller's file identity (name, size, lastModified) plus EVERY
 * setting that changes a pixel, plus the window length — so a checkpoint can
 * never be resumed into a different render. Checkpoints expire after TTL_MS;
 * finished ones are removed once a newer run starts (the page may still be
 * handing the last result over — the final file is made of their bytes).
 *
 *   ResumableRender.supported()              → bool
 *   ResumableRender.canCarrySound(file)      → Promise<bool>  (AAC or no sound)
 *   ResumableRender.peek(file)               → Promise<{key, settings, frac, done, total} | null>
 *   ResumableRender.run({slug, file, settings, durationSec, segSec, signal,
 *                        runSegment, onProgress, onResumed}) → Promise<{blob, frames, segments, resumed}>
 *   ResumableRender.purge(keepKey)           → Promise<void>
 *
 * A refusal throws an Error with `code`: 'rr-unsupported', 'rr-sound' (the
 * source's sound cannot travel by copy — caller renders whole), 'rr-join'
 * (the windows would not join twice — caller renders whole). Cancellation is
 * the caller's own error from runSegment, passed through untouched.
 */
(function (root) {
'use strict';

var DIR = 'rr-checkpoints';
var V = 1;
var TTL_MS = 2 * 864e5;           // an unfinished render waits two days for its file
var DONE_KEEP_MS = 2 * 36e5;      // a finished one: two hours …
var DONE_GRACE_MS = 10 * 6e4;     // … or ten minutes once a newer run starts: the last
                                  // result may still be on its way through the relay
var MUX_PREFIX = '_muxtmp_';      // webcodecs-pipeline.js MUX_TMP_PREFIX

function ae(slug, action, data) {
    try {
        var p = { slug: slug || '', action: action };
        for (var k in (data || {})) if (Object.prototype.hasOwnProperty.call(data, k)) p[k] = data[k];
        if (root.__ae) root.__ae('feature_use', p);
    } catch (_) {}
}
function typed(code, msg) { var e = new Error(msg || code); e.code = code; return e; }

/* OPFS is the one thing a page must have; the join and the sound splice are
   loaded on demand (ensureDeps) — only a long render needs them, and every
   visitor of the tool would otherwise download them. */
function supported() {
    return !!(root.navigator && navigator.storage && typeof navigator.storage.getDirectory === 'function');
}
function loadScript(path) {
    return new Promise(function (res, rej) {
        var s = document.createElement('script');
        s.src = (typeof root.__jsSrc === 'function') ? root.__jsSrc(path) : path;   // versioned, like the pipeline's own
        s.onload = res; s.onerror = function () { rej(typed('rr-unsupported', 'load ' + path)); };
        document.head.appendChild(s);
    });
}
var _deps = null;
function ensureDeps() {
    if (root.Mp4CopyTrim && root.Mp4CopyTrim.concat && root.MP4TrackRemux && root.MP4TrackRemux.replaceAudio) return Promise.resolve();
    if (!_deps) {
        _deps = Promise.all([
            (root.Mp4CopyTrim && root.Mp4CopyTrim.concat) ? null : loadScript('/js/mp4-copy-trim.js'),
            (root.MP4TrackRemux && root.MP4TrackRemux.replaceAudio) ? null : loadScript('/js/mp4-track-remux.js')
        ]).catch(function (e) { _deps = null; throw e; });
    }
    return _deps;
}

/* 64 bits of FNV-1a (two lanes) — a directory name, not a secret. */
function hash(str) {
    var a = 0x811c9dc5, b = 0x9e3779b9;
    for (var i = 0; i < str.length; i++) {
        var c = str.charCodeAt(i);
        a = Math.imul(a ^ c, 16777619);
        b = Math.imul(b ^ c, 2246822519) ^ (b >>> 13);
    }
    return ('0000000' + (a >>> 0).toString(16)).slice(-8) + ('0000000' + (b >>> 0).toString(16)).slice(-8);
}
function stable(o) {
    if (o === null || typeof o !== 'object') return JSON.stringify(o);
    if (Array.isArray(o)) return '[' + o.map(stable).join(',') + ']';
    return '{' + Object.keys(o).sort().map(function (k) { return JSON.stringify(k) + ':' + stable(o[k]); }).join(',') + '}';
}
function fileId(file) { return [String((file && file.name) || ''), (file && file.size) || 0, (file && file.lastModified) || 0].join('|'); }
function keyFor(file, settings, segSec) { return hash(fileId(file)) + '-' + hash(stable(settings || {}) + '|' + segSec); }
function segName(i) { return 'seg_' + ('00' + i).slice(-3) + '.mp4'; }

/* Window boundaries of the SOURCE timeline. The last window ends past the
   source (`dur + 1`) so the final frames are never cut by a duration the
   container rounded; the frames in [a, b) belong to exactly one window. */
function windows(dur, segSec) {
    var out = [];
    for (var a = 0; a < dur - 0.05; a += segSec) out.push([a, Math.min(dur, a + segSec)]);
    if (!out.length) out.push([0, dur]);
    out[out.length - 1][2] = dur + 1;
    return out;
}

async function base(create) { return (await navigator.storage.getDirectory()).getDirectoryHandle(DIR, { create: !!create }); }
async function dirOf(key, create) { try { return await (await base(create)).getDirectoryHandle(key, { create: !!create }); } catch (_) { return null; } }
async function readManifest(dir) {
    try { return JSON.parse(await (await (await dir.getFileHandle('manifest.json')).getFile()).text()); } catch (_) { return null; }
}
async function writeManifest(dir, m) {
    var w = await (await dir.getFileHandle('manifest.json', { create: true })).createWritable();
    try { await w.write(JSON.stringify(m)); await w.close(); } catch (e) { try { await w.abort(); } catch (_) {} throw e; }
}

/* The window's file into the checkpoint directory. The pipeline already wrote
   it to OPFS (`_muxtmp_…`, owned by this page): MOVE it — a copy would hold
   the result twice on a phone's disk for the whole render. Without move() (or
   for a result the pipeline kept in memory) — copy, then drop the temp. */
async function keepWindow(dir, i, r) {
    var name = segName(i), blob = r.blob;
    /* The pipeline names its raw OPFS output (`opfsName`, before the source's
       tags are carried — with tags `blob` is a composite without a name). The
       window keeps the raw file; the tags go onto the joined result once. */
    var tmp = (typeof r.opfsName === 'string' && r.opfsName.indexOf(MUX_PREFIX) === 0) ? r.opfsName
        : (blob && typeof blob.name === 'string' && blob.name.indexOf(MUX_PREFIX) === 0) ? blob.name : '';
    var rootDir = await navigator.storage.getDirectory();
    if (tmp) {
        try {
            var h = await rootDir.getFileHandle(tmp);
            if (typeof h.move === 'function') {
                await h.move(dir, name);
                var moved = await (await dir.getFileHandle(name)).getFile();
                if (moved.size > 0) return moved;
            }
        } catch (_) { /* copy below */ }
    }
    var fh = await dir.getFileHandle(name, { create: true });
    var w = await fh.createWritable();
    try { await w.write(blob); await w.close(); } catch (e) { try { await w.abort(); } catch (_) {} throw e; }
    var copied = await fh.getFile();
    if (copied.size !== blob.size) throw typed('rr-short-copy');
    if (tmp) { try { await rootDir.removeEntry(tmp); } catch (_) {} }
    return copied;
}

async function remove(key) { try { await (await base(false)).removeEntry(key, { recursive: true }); } catch (_) {} }

/* Expired checkpoints, and finished ones a newer run no longer needs. */
async function purge(keepKey) {
    if (!supported()) return;
    var b;
    try { b = await base(false); } catch (_) { return; }
    var names = [];
    try { for await (var e of b.entries()) if (e[1].kind === 'directory') names.push(e[0]); } catch (_) { return; }
    var now = Date.now();
    for (var i = 0; i < names.length; i++) {
        if (names[i] === keepKey) continue;
        var m = await readManifest(await b.getDirectoryHandle(names[i]));
        var age = now - ((m && m.updatedAt) || 0);
        if (!m || age > TTL_MS || (m.done && age > (keepKey !== undefined ? DONE_GRACE_MS : DONE_KEEP_MS))) await remove(names[i]);
    }
}

/* Can the source's sound travel by COPY into the joined file? AAC (the
   pipeline copies it the same way) or no sound at all. Anything else — PCM
   from a camera, AMR, ALAC — the whole render re-encodes; so does it here. */
async function canCarrySound(file) {
    if (!supported()) return false;
    try { await ensureDeps(); } catch (_) { return false; }
    if (!root.MP4TrackRemux.inspect) return false;
    try {
        var info = await root.MP4TrackRemux.inspect(file);
        if (info.fragmented) return false;
        var snd = info.tracks.filter(function (t) { return t.handler === 'soun'; });
        return snd.length === 0 || (snd.length === 1 && snd[0].codec === 'aac');
    } catch (_) { return false; }
}

/* The newest unfinished render of THIS file, whatever its settings — the page
   offers to continue it and puts the settings back. */
async function peek(file) {
    if (!supported()) return null;
    var b;
    try { b = await base(false); } catch (_) { return null; }
    var want = hash(fileId(file)) + '-', best = null;
    try {
        for await (var e of b.entries()) {
            if (e[1].kind !== 'directory' || e[0].indexOf(want) !== 0) continue;
            var m = await readManifest(e[1]);
            if (!m || m.v !== V || m.done || !m.segs || !m.segs.length) continue;
            if (Date.now() - (m.updatedAt || 0) > TTL_MS) continue;
            if (!best || m.updatedAt > best.updatedAt) best = m;
        }
    } catch (_) { return null; }
    if (!best) return null;
    var w = windows(best.dur, best.segSec), doneTo = w[Math.min(best.segs.length, w.length) - 1][1];
    return { key: best.key, settings: best.settings, frac: Math.min(1, doneTo / best.dur), done: best.segs.length, total: w.length, updatedAt: best.updatedAt };
}

async function run(o) {
    if (!supported()) throw typed('rr-unsupported');
    var slug = o.slug || '', file = o.file, dur = +o.durationSec, segSec = o.segSec || 60;
    if (!(dur > 0)) throw typed('rr-unsupported', 'no duration');
    await ensureDeps();
    if (!(await canCarrySound(file))) throw typed('rr-sound');
    var W = windows(dur, segSec), key = keyFor(file, o.settings, segSec);
    var progress = typeof o.onProgress === 'function' ? o.onProgress : function () {};
    await purge(key);

    var dir = await dirOf(key, true);
    var fresh = { v: V, key: key, fileId: fileId(file), settings: o.settings || {}, segSec: segSec, dur: dur,
                  count: W.length, segs: [], done: false, createdAt: Date.now(), updatedAt: Date.now() };
    var m = dir ? await readManifest(dir) : null;
    if (!m || m.v !== V || m.count !== W.length || m.fileId !== fresh.fileId || m.segSec !== segSec || Math.abs((m.dur || 0) - dur) > 1) m = fresh;
    var files = [], frames = 0;
    for (var i = 0; dir && i < m.segs.length; i++) {
        try {
            var f = await (await dir.getFileHandle(segName(i))).getFile();
            if (!f.size || f.size !== m.segs[i].bytes) break;          // a short tail ends the usable prefix
            files.push(f); frames += m.segs[i].frames || 0;
        } catch (_) { break; }
    }
    m.segs = m.segs.slice(0, files.length);
    var resumed = files.length;
    if (resumed) {
        var from = W[resumed - 1][1];
        ae(slug, 'rr_resumed', { segs: resumed, total: W.length, from_s: Math.round(from), age_min: Math.round((Date.now() - (m.updatedAt || Date.now())) / 60000) });
        if (typeof o.onResumed === 'function') { try { o.onResumed({ frac: from / dur, done: resumed, total: W.length }); } catch (_) {} }
        progress(from / dur);
    } else if (dir) {
        try { await writeManifest(dir, m); } catch (_) { dir = null; }
    }

    async function render(k) {
        var a = W[k][0], b = W[k][1], end = W[k][2] || b, t0 = Date.now();
        var r = await o.runSegment(a, end, function (p) { progress((a + Math.max(0, Math.min(1, +p || 0)) * (b - a)) / dur); });
        if (!r || !r.blob || !r.blob.size) throw typed('rr-empty-window', 'window ' + k + ' produced no file');
        var kept = null;
        if (dir) {
            try { kept = await keepWindow(dir, k, r); }
            catch (e) { dir = null; ae(slug, 'rr_checkpoint_off', { at: k, err: String((e && (e.name || e.message)) || e).slice(0, 60) }); }
        }
        ae(slug, 'rr_window', { k: k, of: W.length, ms: Date.now() - t0, mb: Math.round(r.blob.size / 1048576), kept: kept ? 1 : 0 });
        return { file: kept || r.blob, frames: r.frames || 0, kept: !!kept };
    }

    for (var k = files.length; k < W.length; k++) {
        var w = await render(k);
        files[k] = w.file; frames += w.frames;
        if (dir && w.kept) {
            m.segs[k] = { i: k, bytes: w.file.size, frames: w.frames };
            m.updatedAt = Date.now();
            try { await writeManifest(dir, m); } catch (_) { dir = null; }
        }
    }

    // ── Join the windows without re-encoding. A window the plan refuses
    // (an encoder that changed its parameter sets mid-render, say) is rendered
    // again once; a second refusal sends the caller to the whole render.
    var name = (o.name || 'render') + '.mp4', joined = null, redone = {};
    for (var attempt = 0; attempt < 3 && !joined; attempt++) {
        try {
            joined = await root.Mp4CopyTrim.concat({ clips: files.map(function (f) { return { video: f, audio: null }; }), silence: null, brand: 'mp4', name: name });
        } catch (e) {
            var bad = e && typeof e.clip === 'number' ? e.clip : -1;
            ae(slug, 'rr_join_refused', { clip: bad, code: String((e && (e.code || e.message)) || e).slice(0, 40), attempt: attempt });
            if (bad < 0 || redone[bad]) throw typed('rr-join', String((e && (e.code || e.message)) || e));
            redone[bad] = 1;
            var again = await render(bad);
            files[bad] = again.file;
            if (dir && again.kept) { m.segs[bad] = { i: bad, bytes: again.file.size, frames: again.frames }; try { await writeManifest(dir, m); } catch (_) {} }
        }
    }
    if (!joined) throw typed('rr-join');

    // ── The source's own sound, by copy. canCarrySound() vouched for it above.
    var out = joined;
    try {
        var withSound = await root.MP4TrackRemux.replaceAudio(joined, o.soundSource || file);
        out = (withSound && withSound.blob) || withSound || joined;
    } catch (e) {
        if (!(e && /audio-no-track/.test(String(e.remuxReason || e.code || e.message)))) {
            ae(slug, 'rr_sound_refused', { reason: String((e && (e.remuxReason || e.code || e.message)) || e).slice(0, 40) });
            throw typed('rr-sound', String((e && (e.remuxReason || e.message)) || e));
        }
    }
    // The source's descriptive tags (title, artist…) — the whole render carries
    // them in the pipeline; the windows were kept raw, so they go on here once.
    if (root.__mp4Tags && typeof root.__mp4Tags.carry === 'function') {
        try { out = await root.__mp4Tags.carry(file, out, { slug: slug }); } catch (_) {}
    }
    if (dir) { m.done = true; m.updatedAt = Date.now(); try { await writeManifest(dir, m); } catch (_) {} }
    ae(slug, 'rr_done', { segs: W.length, resumed: resumed, mb: Math.round(out.size / 1048576) });
    return { blob: out, frames: frames, segments: W.length, resumed: resumed };
}

root.ResumableRender = {
    supported: supported, canCarrySound: canCarrySound, peek: peek, run: run, purge: purge,
    keyFor: keyFor, windows: windows, remove: remove
};
})(typeof window !== 'undefined' ? window : globalThis);
